import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api, describeError, getBaseUrl, getTokens } from '../api';
import { Json, Message, Pill, Section } from '../components/ui';
import { useSession } from '../session';

/** change 13: POST /api/characters/ai/generate (SSE) · /complete · /regenerate — stateless drafts, saving = POST /api/characters. */
export interface Card { spec: string; spec_version: string; data: Record<string, unknown> & { name: string; description?: string; personality?: string; scenario?: string; first_mes?: string; alternate_greetings?: string[]; mes_example?: string; tags?: string[]; extensions?: Record<string, unknown> } }
interface Draft { name: string; tagline: string; tags: string[]; description: string; personality: string; scenario: string; greetings: string[]; mes_example: string; creator_notes?: string }
interface Receipt { usage: Record<string, number | undefined>; credits_charged: number | null; balance_after: number | null; model: string; channel: string; charged: boolean; attempts: number }
interface StartInfo { request_id: string; model: string; channel: string; charged: boolean; reserved_credits: number | null; estimate_tokens: number }
interface Profile { id: string; name: string; extensions?: { pockettavern?: { credential_source?: string; model_id?: string } } }
type Field = 'description' | 'personality' | 'scenario' | 'first_mes' | 'alternate_greetings' | 'mes_example' | 'tagline' | 'tags';

const PHASES: Array<[number, string]> = [[0, '构建人设主体（Description）…'], [0.3, '推理性格特征（Personality）…'], [0.5, '生成初始场景（Scenario）…'], [0.65, '撰写开场白…'], [0.85, '添加示例对话…'], [0.97, '完成 · 正在整理…']];

async function postSse(path: string, body: unknown, onFrame: (event: string, data: Record<string, unknown>) => void, signal: AbortSignal): Promise<void> {
  const headers: Record<string, string> = { Accept: 'text/event-stream', 'Content-Type': 'application/json' };
  const tokens = getTokens();
  if (tokens) headers.Authorization = `Bearer ${tokens.access_token}`;
  const res = await fetch(`${getBaseUrl()}${path}`, { method: 'POST', headers, body: JSON.stringify(body), signal });
  if (!res.ok || !res.body) {
    const env = (await res.json().catch(() => null)) as { error?: { code: string; message: string } & Record<string, unknown> } | null;
    throw new ApiFailure(res.status, env?.error ?? { code: 'CONSOLE_BAD_RESPONSE', message: `HTTP ${res.status}` }, res.headers.get('X-Request-Id'));
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = '';
      let data: Record<string, unknown> = {};
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        else if (line.startsWith('data: ')) data = JSON.parse(line.slice(6)) as Record<string, unknown>;
      }
      if (event) onFrame(event, data);
    }
  }
}

/** Editor helper: fill empty fields of the current draft. */
export async function aiComplete(card: unknown, model?: string): Promise<{ card: Card; filled_fields: Field[]; receipt: Receipt }> {
  const { data } = await api<{ card: Card; filled_fields: Field[] } & Receipt>('/api/characters/ai/complete', { method: 'POST', body: { card, ...(model ? { model } : {}) } });
  const { card: out, filled_fields, ...receipt } = data;
  return { card: out, filled_fields, receipt };
}

export function receiptText(r: Receipt | null): string {
  if (!r) return '';
  const cost = r.charged ? `扣 ${r.credits_charged ?? '?'} 钻石 · 余额 ${r.balance_after ?? '?'}` : `${r.channel} 不计费`;
  return `${r.model} · ${r.usage.total_tokens ?? '?'} tokens · ${cost} · ${r.attempts} 次调用`;
}

export function CharacterAiPanel({ onSaved }: { onSaved?: (characterId: string) => void }) {
  const { me } = useSession();
  const [daily, setDaily] = useState<{ limit: number | null; count: number; resets_at: string } | null>(null);
  const [prompt, setPrompt] = useState('一个 25 岁的都市咖啡店店长 · 表面冷淡，其实关心每一位常客 · 喜欢在雨天读诗');
  const [detail, setDetail] = useState<'brief' | 'balanced' | 'detailed'>('balanced');
  const [style, setStyle] = useState<'balanced' | 'literary' | 'playful' | 'concise'>('literary');
  const [greetings, setGreetings] = useState(2);
  const [language, setLanguage] = useState<'auto' | 'zh' | 'en' | 'ja'>('auto');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [profileId, setProfileId] = useState('');
  const [phase, setPhase] = useState<'input' | 'generating' | 'result'>('input');
  const [start, setStart] = useState<StartInfo | null>(null);
  const startRef = useRef<StartInfo | null>(null); // message_end runs inside the SSE callback where `start` state is stale
  const [chars, setChars] = useState(0);
  const [card, setCard] = useState<Card | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busyField, setBusyField] = useState<string | null>(null);
  const [abort, setAbort] = useState<AbortController | null>(null);
  const [showJson, setShowJson] = useState(false);

  useEffect(() => {
    if (!me) return;
    api<Profile[]>('/api/connection-profiles').then((r) => setProfiles(r.data)).catch(() => setProfiles([]));
    api<{ limits: { ai_character_gen_per_day: { limit: number | null; count: number; resets_at: string } } }>('/api/me/entitlements')
      .then((r) => setDaily(r.data.limits.ai_character_gen_per_day))
      .catch(() => setDaily(null));
  }, [me]);

  const gated = me && !me.features.ai_character_gen;
  const route = profileId ? { profile_id: profileId } : {};
  const expected = start?.estimate_tokens ?? 1200;
  const progress = Math.min(0.97, chars / (expected * 2.5)); // ~2.5 chars per token as a rough progress proxy
  const phaseLabel = [...PHASES].reverse().find(([p]) => progress >= p)?.[1] ?? PHASES[0]![1];

  const generate = async () => {
    setError(null);
    setMessage(null);
    setCard(null);
    setDraft(null);
    setReceipt(null);
    setStart(null);
    startRef.current = null;
    setChars(0);
    setPhase('generating');
    const ctl = new AbortController();
    setAbort(ctl);
    try {
      await postSse('/api/characters/ai/generate', { prompt, options: { detail_level: detail, style, greetings, language }, ...route }, (event, data) => {
        if (event === 'message_start') {
          startRef.current = data as unknown as StartInfo;
          setStart(startRef.current);
        }
        else if (event === 'delta') setChars((n) => n + String(data.text ?? '').length);
        else if (event === 'card') {
          setCard(data.card as Card);
          setDraft(data.draft as Draft);
        } else if (event === 'message_end') setReceipt({ ...(data as unknown as Receipt), model: startRef.current?.model ?? '', channel: startRef.current?.channel ?? 'official', charged: startRef.current?.charged ?? true });
        else if (event === 'error') throw new ApiFailure(502, data as { code: string; message: string } & Record<string, unknown>, null);
      }, ctl.signal);
      setPhase('result');
    } catch (err) {
      if (ctl.signal.aborted) setMessage('已中断生成');
      else setError(describeError(err));
      setPhase(card ? 'result' : 'input');
    } finally {
      setAbort(null);
    }
  };

  const regenerate = async (field: Field, instruction?: string) => {
    if (!card) return;
    setBusyField(field);
    setError(null);
    try {
      const { data } = await api<{ field: Field; value: string | string[] } & Receipt>('/api/characters/ai/regenerate', { method: 'POST', body: { card, field, ...(instruction ? { instruction } : {}), options: { style, language }, ...route } });
      const next = structuredClone(card) as Card;
      const ext = { ...((next.data.extensions ?? {}) as Record<string, unknown>) };
      const pt = { ...((ext.pockettavern as Record<string, unknown> | undefined) ?? {}) };
      if (field === 'tagline') pt.tagline = data.value;
      else if (field === 'tags' || field === 'alternate_greetings') (next.data as Record<string, unknown>)[field] = data.value;
      else (next.data as Record<string, unknown>)[field] = data.value;
      ext.pockettavern = pt;
      next.data.extensions = ext;
      setCard(next);
      if (draft) setDraft({ ...draft, ...(field === 'first_mes' ? { greetings: [String(data.value), ...draft.greetings.slice(1)] } : field === 'alternate_greetings' ? { greetings: [draft.greetings[0] ?? '', ...(data.value as string[])] } : { [field]: data.value }) } as Draft);
      const { field: _f, value: _v, ...r } = data;
      setMessage(`已重生成 ${field} · ${receiptText(r)}`);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyField(null);
    }
  };

  const save = async (andChat: boolean) => {
    if (!card) return;
    setError(null);
    try {
      const { data } = await api<{ id: string; name: string }>('/api/characters', { method: 'POST', body: { card } });
      let text = `已保存到角色库：${data.name}（${data.id.slice(0, 8)}）`;
      if (andChat) {
        const chat = await api<{ id: string }>('/api/chats', { method: 'POST', body: { character_id: data.id } });
        text += ` · 已建会话 ${chat.data.id.slice(0, 8)}，到「聊天」页选择该角色即可开聊`;
      }
      setMessage(text);
      onSaved?.(data.id);
    } catch (err) {
      setError(describeError(err));
    }
  };

  if (!me) return null;
  const fields: Array<[Field, string, string]> = [
    ['description', 'Description', '外貌 · 背景 · 关系网'],
    ['personality', 'Personality', '性格摘要'],
    ['scenario', 'Scenario', '初始场景'],
    ['mes_example', '示例对话', '<START> 分隔'],
  ];
  const pt = (card?.data.extensions?.pockettavern ?? {}) as { tagline?: string; avatar_color?: string; avatar_text?: string };
  return (
    <Section
      title="AI 生成角色 · POST /api/characters/ai/generate（Pro）"
      actions={<>{phase === 'result' && <button className="btn-ghost" onClick={() => void generate()}>重新生成</button>}{phase === 'generating' && <button className="btn-ghost" onClick={() => abort?.abort()}>中断</button>}</>}
    >
      <Message text={error} kind="error" />
      <Message text={message} kind="ok" />
      {gated && <Message text="当前账号没有 ai_character_gen（Pro 专属）：三个端点都会 403 PLAN_FEATURE_REQUIRED。到钻石页模拟购买订阅后重新登录即可。" />}
      <textarea className="input font-sans text-sm" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="一句话描述角色…" disabled={phase === 'generating'} />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1 text-zinc-400">篇幅<select className="input py-0.5" value={detail} onChange={(e) => setDetail(e.target.value as typeof detail)}><option value="brief">brief（≈600）</option><option value="balanced">balanced（≈1200）</option><option value="detailed">detailed（≈2000）</option></select></label>
        <label className="flex items-center gap-1 text-zinc-400">风格<select className="input py-0.5" value={style} onChange={(e) => setStyle(e.target.value as typeof style)}>{['balanced', 'literary', 'playful', 'concise'].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
        <label className="flex items-center gap-1 text-zinc-400">开场白<select className="input py-0.5" value={greetings} onChange={(e) => setGreetings(Number(e.target.value))}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
        <label className="flex items-center gap-1 text-zinc-400">语言<select className="input py-0.5" value={language} onChange={(e) => setLanguage(e.target.value as typeof language)}>{['auto', 'zh', 'en', 'ja'].map((l) => <option key={l} value={l}>{l}</option>)}</select></label>
        <label className="flex items-center gap-1 text-zinc-400">档案<select className="input py-0.5" value={profileId} onChange={(e) => setProfileId(e.target.value)}><option value="">激活档案 / 平台默认</option>{profiles.map((p) => <option key={p.id} value={p.id}>{p.name}{p.extensions?.pockettavern?.credential_source ? ` (${p.extensions.pockettavern.credential_source})` : ''}</option>)}</select></label>
        <button className="btn" disabled={phase === 'generating' || !prompt.trim()} onClick={() => void generate()}>✨ 生成</button>
      </div>
      {phase === 'generating' && (
        <div className="space-y-1 rounded-lg border border-zinc-800 p-3 text-xs">
          <div className="flex justify-between text-zinc-300"><span>{phaseLabel}</span><span>{chars} 字</span></div>
          <div className="h-1.5 rounded bg-zinc-800"><div className="h-1.5 rounded bg-amber-400/70 transition-all" style={{ width: `${Math.max(3, progress * 100)}%` }} /></div>
          {start && <div className="text-zinc-500">模型 {start.model} · {start.channel} · {start.charged ? `预扣 ${start.reserved_credits} 钻石（一口价 / 轮；估算 ${start.estimate_tokens} token 仅对账）` : '不计费'} · request {start.request_id.slice(0, 8)}</div>}
        </div>
      )}
      {card && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg text-lg font-semibold text-zinc-100" style={{ background: pt.avatar_color ?? '#3f3f46' }}>{pt.avatar_text ?? card.data.name[0]}</div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">{card.data.name}<Pill tone="indigo">ai_generated</Pill>
                <button className="btn-ghost py-0 text-[11px]" disabled={!!busyField} onClick={() => void regenerate('tagline')}>🔄 tagline</button>
              </div>
              <div className="truncate text-xs text-zinc-400">{pt.tagline}</div>
              <div className="flex flex-wrap gap-1 text-[11px] text-zinc-500">{(card.data.tags ?? []).map((t) => <span key={t}>#{t}</span>)}<button className="btn-ghost py-0 text-[11px]" disabled={!!busyField} onClick={() => void regenerate('tags')}>🔄 tags</button></div>
            </div>
          </div>
          {fields.map(([key, label, hint]) => (
            <div key={key} className="rounded-lg border border-zinc-800">
              <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-1.5 text-xs"><span className="font-medium text-zinc-200">{label} <span className="text-zinc-500">{hint}</span></span><RegenButton busy={busyField === key} onRun={(ins) => void regenerate(key, ins)} /></div>
              <div className="whitespace-pre-wrap px-3 py-2 text-xs text-zinc-300">{String(card.data[key] ?? '')}</div>
            </div>
          ))}
          <div className="rounded-lg border border-zinc-800">
            <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-1.5 text-xs"><span className="font-medium text-zinc-200">开场白 · {1 + (card.data.alternate_greetings?.length ?? 0)} 个 <span className="text-zinc-500">聊天中可 Swipe 切换</span></span><div className="flex gap-1"><RegenButton label="🔄 首条" busy={busyField === 'first_mes'} onRun={(ins) => void regenerate('first_mes', ins)} />{(card.data.alternate_greetings?.length ?? 0) > 0 && <RegenButton label="🔄 备选" busy={busyField === 'alternate_greetings'} onRun={(ins) => void regenerate('alternate_greetings', ins)} />}</div></div>
            <div className="divide-y divide-zinc-800">
              {[card.data.first_mes ?? '', ...(card.data.alternate_greetings ?? [])].map((g, i) => <div key={i} className="px-3 py-2 text-xs text-zinc-300"><div className="text-[10px] text-zinc-500">开场白 {i + 1}</div><div className="whitespace-pre-wrap">{g}</div></div>)}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button className="btn" onClick={() => void save(false)}>保存到角色库</button>
            <button className="btn" onClick={() => void save(true)}>保存 + 开始聊</button>
            <button className="btn-ghost" onClick={() => setShowJson(!showJson)}>{showJson ? '收起' : '查看'} CCv3 JSON</button>
            {receipt && <span className="text-zinc-500">{receiptText(receipt)}</span>}
          </div>
          {showJson && <Json value={{ card, draft }} className="max-h-72" />}
        </div>
      )}
      <p className="text-[11px] text-zinc-500">流程：generate（SSE：message_start 预扣 → delta 进度 → card 归一化 CCv3 → message_end 实扣）→ 字段级 regenerate（可带指令）→ POST /api/characters 保存 →（可选）POST /api/chats。草稿无 gender。字数：名称 ≤ 40。tagline、description、personality、scenario、first_mes、mes_example 不限长度。全部端点不落库、官方池按轮一口价扣钻石、计入 ai_character_gen_per_day{daily ? `（今日 ${daily.count} / ${daily.limit === null ? '∞' : daily.limit}，达限 403 PLAN_LIMIT_REACHED）` : ''}。</p>
    </Section>
  );
}

function RegenButton({ label = '🔄 重生成此项', busy, onRun }: { label?: string; busy: boolean; onRun: (instruction?: string) => void }) {
  const [open, setOpen] = useState(false);
  const [ins, setIns] = useState('');
  return (
    <span className="flex items-center gap-1">
      {open && <input className="input w-40 py-0 text-[11px]" placeholder="附加指令（可选）" value={ins} onChange={(e) => setIns(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { onRun(ins || undefined); setOpen(false); } }} />}
      <button className="btn-ghost py-0 text-[11px]" disabled={busy} onClick={() => { if (open) { onRun(ins || undefined); setOpen(false); } else setOpen(true); }}>{busy ? '生成中…' : open ? '确定' : label}</button>
    </span>
  );
}
